// src/components/lists/AccountsList/InvoiceList.tsx

import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { InvoiceSummary } from '../../../services/api';
import AccountsList, {
  Column,
  DateCell,
  ChipCell,
  CurrencyCell,
  LinkCell,
  ActionCell,
} from './AccountsList';
import { InvoiceItem, statusColor, formatCurrency } from './accountTypes';

interface InvoiceListProps {
  invoices: InvoiceSummary[];
  onDownloadPdf: (invoiceId: number) => void;
  onPayInvoice: (invoiceId: number) => void;
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  onDownloadPdf,
  onPayInvoice,
}) => {
  const navigate = useNavigate();

  const invoiceColumns: Column<InvoiceItem>[] = [
    {
      field: 'invoiceNo',
      label: 'INVOICE #',
      width: 220,
      render: (item) => <Box>{item.invoiceNo}</Box>,
    },
    {
      field: 'date',
      label: 'DATE',
      width: 175,
      render: (item) => <DateCell>{item.date}</DateCell>,
    },
    {
      field: 'booking',
      label: 'BOOKING',
      width: 115,
      render: (item) => (
        <LinkCell>
          <Button
            size="small"
            variant="text"
            onClick={() => navigate(`/booking/${item.booking}`)}
          >
            #{item.booking}
          </Button>
        </LinkCell>
      ),
    },
    {
      field: 'total',
      label: 'TOTAL',
      width: 185,
      render: (item) => (
        <CurrencyCell>{formatCurrency(item.total)}</CurrencyCell>
      ),
    },
    {
      field: 'paid',
      label: 'PAID',
      width: 185,
      render: (item) => (
        <CurrencyCell>{formatCurrency(item.paid)}</CurrencyCell>
      ),
    },
    {
      field: 'outstanding',
      label: 'OUTSTANDING',
      width: 195,
      render: (item) => (
        <CurrencyCell>{formatCurrency(item.outstanding)}</CurrencyCell>
      ),
    },
    {
      field: 'status',
      label: 'STATUS',
      width: 160,
      render: (item) => (
        <ChipCell>
          <Chip
            label={item.status}
            size="small"
            color={statusColor(item.status)}
          />
        </ChipCell>
      ),
    },
    {
      label: '',
      width: 260,
      mobileWidth: 300,
      render: (item) => (
        <ActionCell>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => onDownloadPdf(item.id)}
          >
            PDF
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={item.status === 'Paid' || item.status === 'Cancelled'}
            onClick={() => onPayInvoice(item.id)}
          >
            Pay
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/booking/${item.booking}`)}
          >
            View
          </Button>
        </ActionCell>
      ),
    },
  ];

  const invoiceItems: InvoiceItem[] = invoices.map((inv) => ({
    id: inv.id,
    invoiceNo: inv.invoiceNumber,
    date: inv.invoiceDate.split('T')[0],
    booking: inv.bookingId?.toString() ?? '',
    total: inv.totalAmount,
    paid: inv.paidAmount,
    outstanding: inv.outstandingAmount,
    status: inv.status,
  }));

  if (invoices.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <AccountBalanceIcon sx={{ fontSize: '3rem', opacity: 0.8, mb: 1 }} />
        <Typography variant="body1" sx={{ fontSize: '2rem' }}>
          No invoices yet
        </Typography>
      </Box>
    );
  }

  return <AccountsList items={invoiceItems} columns={invoiceColumns} />;
};

export default InvoiceList;
